<script setup lang="ts">
import RlErrorAlert from '@/components/common/RlErrorAlert.vue'
import InboundShipmentForm from '@/components/rc/shipments/forms/InboundShipmentForm.vue'
import OutboundShipmentForm from '@/components/rc/shipments/forms/OutboundShipmentForm.vue'
import { useShipments } from '@/composables/rc/shipments'
import type { IShipmentFormInbound, IShipmentFormOutbound } from '@/types/rc/forms'
import { ShipmentType, type IShipment, type IShipmentReceipt } from '@/types/rc/shipments'
import type { UploadFile } from 'element-plus'
import { computed, ref, watch } from 'vue'

const open = defineModel<boolean>('open')
const props = defineProps<{
  shipment?: Partial<IShipment>
  workOrderNumber?: string
  carriersOptions: string[]
  kind?: ShipmentType
}>()
const emit = defineEmits(['closed'])

const shipmentType = ref<ShipmentType>(props.kind ?? ShipmentType.Inbound)

const carrierOptions = computed(() => {
  return props.carriersOptions.map((carrier: string) => ({
    label: carrier,
    value: carrier,
  }))
})

const addReceiptsToForm = (form: IShipmentFormInbound | IShipmentFormOutbound, receipts: IShipmentReceipt[]) => {
  form.receipts = receipts.map(
    (rec: IShipmentReceipt) =>
      ({
        name: rec.name,
        url: rec.file_url,
        status: 'success',
        uid: rec.id,
      }) as UploadFile
  )
}

watch(
  () => props.shipment,
  (newShipment: Partial<IShipment> | undefined) => {
    if (newShipment) {
      const { type, ...form } = newShipment
      shipmentType.value = type!

      if (shipmentType.value == ShipmentType.Inbound) {
        inboundForm.value = form as unknown as IShipmentFormInbound
        addReceiptsToForm(inboundForm.value, form.receipts ?? [])
      } else if (shipmentType.value == ShipmentType.Outbound) {
        outboundForm.value = form as unknown as IShipmentFormOutbound
        addReceiptsToForm(outboundForm.value, form.receipts ?? [])
      }
    }
  },
  { immediate: true }
)

// Todo: set shipment type based on props.

const defaultInboundForm = {
  work_order: props.workOrderNumber
    ? {
        number: props.workOrderNumber,
        customer: undefined,
      }
    : undefined,
  tracking_information: {
    carrier: '',
    tracking_number: '',
    bol_number: '',
    waybill_number: '',
  },
  expected_containers: '',
  expected_miners: '',
  expected_container_types: [],
  picked_up_date: undefined,
  delivered_date: undefined,
  customer_drop_off: undefined,
  receipts: [],
  deleted_receipt_ids: [],
}

const defaultOutboundForm = {
  work_order: props.workOrderNumber
    ? {
        number: props.workOrderNumber,
        customer: undefined,
      }
    : undefined,
  tracking_information: {
    carrier: '',
    tracking_number: '',
    bol_number: '',
    waybill_number: '',
  },
  expected_containers: '',
  expected_miners: '',
  expected_container_types: [],
  picked_up_date: undefined,
  delivered_date: undefined,
  customer_pick_up: undefined,
  receipts: [],
  deleted_receipt_ids: [],
}

// Inbound form ref and default state.
const inboundFormRef = ref()
const inboundForm = ref<IShipmentFormInbound>(structuredClone(defaultInboundForm))

// Outbound form ref and default state.
const outboundFormRef = ref()
const outboundForm = ref<IShipmentFormOutbound>(structuredClone(defaultOutboundForm))

/**
 * Handles form submit (based on shipment type).
 */
const submitForm = async () => {
  if (shipmentType.value === ShipmentType.Inbound) {
    const valid = await inboundFormRef.value?.isValid()
    if (!valid) return

    if (props.shipment?.id) {
      await updateInboundAsync(inboundForm.value)
    } else {
      await createInboundAsync(inboundForm.value)
    }

    open.value = false
  } else if (shipmentType.value === ShipmentType.Outbound) {
    const valid = await outboundFormRef.value?.isValid()
    if (!valid) return

    if (props.shipment?.id) {
      await updateOutboundAsync(outboundForm.value)
    } else {
      await createOutboundAsync(outboundForm.value)
    }

    open.value = false
  }
}

const {
  createInboundShipmentMutation,
  updateInboundShipmentMutation,
  createOutboundShipmentMutation,
  updateOutboundShipmentMutation,
  isAnyMutationPending,
  isAnyMutationSucceeded,
  resetAllMutations,
} = useShipments()

// Tanstack Vue Query mutation (Create Inbound Shipment)
const {
  mutateAsync: createInboundAsync,
  isError: createInboundIsError,
  error: createInboundError,
} = createInboundShipmentMutation

// Tanstack Vue Query Mutation (Update Inbound Shipment)
const {
  mutateAsync: updateInboundAsync,
  isError: updateInboundIsError,
  error: updateInboundError,
} = updateInboundShipmentMutation

// Tanstack Vue Query mutation (Outbound Shipment)
const {
  mutateAsync: createOutboundAsync,
  isError: createOutboundIsError,
  error: createOutboundError,
} = createOutboundShipmentMutation

// Tanstack Vue Query mutation (Outbound Shipment)
const {
  mutateAsync: updateOutboundAsync,
  isError: updateOutboundIsError,
  error: updateOutboundError,
} = updateOutboundShipmentMutation

// Should form be locked? (Yes, if loading or success!)
const lockForm = computed(() => isAnyMutationPending.value || isAnyMutationSucceeded.value)

/**
 * Clears forms, resets dynamic form groups (damages, containers),
 * resets mutations.
 */
const handleClosed = () => {
  // Reset form fields and validation state
  inboundFormRef.value?.reset()
  outboundFormRef.value?.reset()

  // Reset form refs to "create" state
  inboundForm.value = structuredClone(defaultInboundForm)
  outboundForm.value = structuredClone(defaultOutboundForm)

  // Reset shipment type radio group
  shipmentType.value = ShipmentType.Inbound

  // Reset mutations
  resetAllMutations()

  emit('closed')
}

const handleOpen = () => {
  shipmentType.value = props.kind ?? ShipmentType.Inbound
}
</script>

<template>
  <el-dialog v-model="open" width="570" class="dialog" :show-close="false" @open="handleOpen" @closed="handleClosed">
    <template #header
      ><h3>{{ shipment ? 'Update' : 'Create' }} {{ shipmentType }} Shipment</h3></template
    >
    <div class="dialog__content">
      <el-radio-group
        v-model="shipmentType"
        size="large"
        fill="var(--fill-tertiary)"
        :disabled="lockForm || !!shipment || !!kind"
      >
        <el-radio-button :label="ShipmentType.Inbound" :value="ShipmentType.Inbound" />
        <el-radio-button :label="ShipmentType.Outbound" :value="ShipmentType.Outbound" />
      </el-radio-group>
      <div v-show="shipmentType === ShipmentType.Inbound" class="form-wrapper">
        <InboundShipmentForm
          ref="inboundFormRef"
          v-model="inboundForm"
          :carrier-options="carrierOptions"
          :disabled="lockForm"
          :lock-work-order="!!props.workOrderNumber"
        />
        <RlErrorAlert v-if="createInboundIsError" :error="createInboundError" />
        <RlErrorAlert v-if="updateInboundIsError" :error="updateInboundError" />
      </div>
      <div v-show="shipmentType === ShipmentType.Outbound" class="form-wrapper">
        <OutboundShipmentForm
          ref="outboundFormRef"
          v-model="outboundForm"
          :carrier-options="carrierOptions"
          :disabled="lockForm"
          :lock-work-order="!!props.workOrderNumber"
        />
        <RlErrorAlert v-if="createOutboundIsError" :error="createOutboundError" />
        <RlErrorAlert v-if="updateOutboundIsError" :error="updateOutboundError" />
      </div>
    </div>
    <template #footer>
      <el-button @click="open = false">Cancel</el-button>
      <el-button type="primary" :loading="isAnyMutationPending" @click="submitForm">Submit</el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.dialog {
  &__content {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-spacing-xl);
  }
}

.el-radio-group {
  width: 100%;

  .el-radio-button {
    flex: 1;

    ::v-deep(.el-radio-button__inner) {
      width: 100%;
    }
  }
}

/* Wraps form and error alert */
.form-wrapper {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-spacing-md);
}
</style>
